// src/admin/pages/CapRaiseAdminPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  DollarSign, 
  HelpCircle
} from 'lucide-react';
import MasterAdminHeader from '../components/MasterAdminHeader';
import MasterAdminSidebar from '../components/MasterAdminSidebar';
import { useGlobalStore } from '../../state/globalStore';
import CapRaiseRoundsTab from './tabs/CapRaiseRoundsTab';
import CapRaiseQuestionsTab from './tabs/CapRaiseQuestionsTab';

const CapRaiseAdminPage = () => {
  const { user, userProfile } = useGlobalStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get active tab from URL or default to rounds
  const getActiveTabFromPath = () => {
    const path = location.pathname.replace('/adminhq/capraise', '').replace(/^\//, '');
    if (!path || path === '') return 'rounds';
    return path;
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());

  // CapRaise-specific navigation items
  const capRaiseNavItems = [
    { id: 'rounds', name: 'Rounds', icon: DollarSign, color: 'blue' },
    { id: 'questions', name: 'Questions', icon: HelpCircle, color: 'green' },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/adminhq/capraise/${tabId}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'rounds':
        return <CapRaiseRoundsTab />;
      case 'questions':
        return <CapRaiseQuestionsTab />;
      default:
        return <CapRaiseRoundsTab />;
    }
  };

  // Redirect to first tab if no tab is specified
  useEffect(() => {
    const path = location.pathname.replace('/adminhq/capraise', '').replace(/^\//, '');
    if (!path || path === '') {
      navigate('/adminhq/capraise/rounds', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <MasterAdminSidebar activeTab="capraise" setActiveTab={(tab) => navigate(`/adminhq/${tab}`)} />
      <div className="flex-1 flex flex-col">
        <MasterAdminHeader 
          user={user} 
          userProfile={userProfile} 
          title="CapRaise Admin"
        />
        
        {/* Tabs Bar */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex space-x-1">
            {capRaiseNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === item.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.name}
                </div>
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CapRaiseAdminPage;
